import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { API, useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { 
  MessageSquare, Send, Users, Heart, Sparkles, 
  ChevronDown, ChevronUp, RefreshCw, Circle, AlertTriangle
} from 'lucide-react';

// Icon mapping for chat rooms
const ROOM_ICONS = {
  MessageSquare, Heart, Users, Sparkles
};

// Polling interval in milliseconds
const POLL_INTERVAL = 3000;

export default function CommunityChat({ isOpen, onToggle }) {
  const { user, token } = useAuth();
  const { currentColors } = useTheme();
  const [rooms, setRooms] = useState([]);
  const [activeRoom, setActiveRoom] = useState('general');
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [lastTimestamp, setLastTimestamp] = useState(null);
  const [chatAccess, setChatAccess] = useState({ has_access: true, message: null });
  const messagesEndRef = useRef(null);
  const pollingRef = useRef(null);

  // Check chat access
  useEffect(() => {
    if (token) {
      checkChatAccess();
    }
  }, [token]);

  const checkChatAccess = async () => {
    try {
      const response = await axios.get(`${API}/community/chat-access`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChatAccess(response.data);
    } catch (error) {
      console.error('Failed to check chat access:', error);
    }
  };

  // Scroll to bottom of messages
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Load chat rooms
  useEffect(() => {
    loadRooms();
  }, []);

  // Poll for new messages when chat is open
  useEffect(() => {
    if (isOpen && chatAccess.has_access) {
      loadMessages(activeRoom);
      loadOnlineUsers(activeRoom);
      
      // Start polling
      pollingRef.current = setInterval(() => {
        pollNewMessages();
        loadOnlineUsers(activeRoom);
      }, POLL_INTERVAL);
      
      return () => {
        if (pollingRef.current) {
          clearInterval(pollingRef.current);
        }
      };
    }
  }, [isOpen, activeRoom, chatAccess.has_access]);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const loadRooms = async () => {
    try {
      const response = await axios.get(`${API}/chat/rooms`);
      setRooms(response.data);
    } catch (error) {
      console.error('Failed to load chat rooms:', error);
    }
  };

  const loadMessages = async (roomId) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/chat/messages/${roomId}?limit=50`);
      setMessages(response.data.messages || []);
      setLastTimestamp(response.data.timestamp);
    } catch (error) {
      console.error('Failed to load messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const pollNewMessages = useCallback(async () => {
    if (!lastTimestamp) return;
    
    try {
      const response = await axios.get(
        `${API}/chat/messages/${activeRoom}?since=${lastTimestamp}&limit=20`
      );
      
      const newMsgs = response.data.messages || [];
      if (newMsgs.length > 0) {
        setMessages(prev => {
          // Merge and dedupe messages
          const existing = new Set(prev.map(m => m.id));
          const unique = newMsgs.filter(m => !existing.has(m.id));
          return [...prev, ...unique];
        });
      }
      setLastTimestamp(response.data.timestamp);
    } catch (error) {
      console.error('Polling error:', error);
    }
  }, [lastTimestamp, activeRoom]);

  const loadOnlineUsers = async (roomId) => {
    try {
      const response = await axios.get(`${API}/chat/online-users/${roomId}`);
      setOnlineUsers(response.data.online_users || []);
    } catch (error) {
      console.error('Failed to load online users:', error);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!token || !newMessage.trim() || sending) return;
    
    setSending(true);
    try {
      const response = await axios.post(
        `${API}/chat/messages`,
        { content: newMessage, room_id: activeRoom },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Add message optimistically
      setMessages(prev => [...prev, response.data]);
      setNewMessage('');
    } catch (error) {
      console.error('Failed to send message:', error);
      alert('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const switchRoom = (roomId) => {
    setActiveRoom(roomId);
    setMessages([]);
    setLastTimestamp(null);
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getRoomIcon = (iconName) => {
    return ROOM_ICONS[iconName] || MessageSquare;
  };

  const activeRoomInfo = rooms.find(r => r.id === activeRoom) || { name: 'Chat', icon: 'MessageSquare' };
  const ActiveIcon = getRoomIcon(activeRoomInfo.icon);

  if (!isOpen) {
    return (
      <button
        onClick={onToggle}
        className="fixed bottom-20 right-6 w-14 h-14 rounded-full flex items-center justify-center shadow-lg transition-all hover:scale-110 z-40"
        style={{ 
          backgroundColor: currentColors.primary,
          boxShadow: `0 4px 20px ${currentColors.primary}40`
        }}
        data-testid="chat-toggle-btn"
      >
        <MessageSquare className="w-6 h-6 text-white" />
        {onlineUsers.length > 0 && (
          <span 
            className="absolute -top-1 -right-1 w-5 h-5 rounded-full text-xs flex items-center justify-center text-white"
            style={{ backgroundColor: '#22c55e' }}
          >
            {onlineUsers.length}
          </span>
        )}
      </button>
    );
  }

  return (
    <div 
      className="fixed bottom-20 right-6 w-96 max-w-[calc(100vw-2rem)] rounded-2xl shadow-2xl z-40 overflow-hidden"
      style={{ 
        backgroundColor: currentColors.background,
        border: `1px solid ${currentColors.border}`,
        boxShadow: `0 25px 50px -12px rgba(0,0,0,0.5)`
      }}
      data-testid="community-chat"
    >
      {/* Header */}
      <div 
        className="p-4 flex items-center justify-between cursor-pointer"
        style={{ 
          background: `linear-gradient(135deg, ${currentColors.primary}20, ${currentColors.secondary}20)`,
          borderBottom: `1px solid ${currentColors.border}`
        }}
        onClick={onToggle}
      >
        <div className="flex items-center gap-3">
          <ActiveIcon className="w-5 h-5" style={{ color: currentColors.primary }} />
          <div>
            <h3 className="font-cinzel font-medium" style={{ color: currentColors.text }}>
              {activeRoomInfo.name}
            </h3>
            <div className="flex items-center gap-1 text-xs" style={{ color: currentColors.textMuted }}>
              <Circle className="w-2 h-2 fill-green-500 text-green-500" />
              <span>{onlineUsers.length} online</span>
            </div>
          </div>
        </div>
        <ChevronDown className="w-5 h-5" style={{ color: currentColors.textMuted }} />
      </div>

      {/* Room Tabs */}
      <div 
        className="flex gap-1 p-2 overflow-x-auto"
        style={{ backgroundColor: `${currentColors.primary}05` }}
      >
        {rooms.map(room => {
          const Icon = getRoomIcon(room.icon);
          return (
            <button
              key={room.id}
              onClick={() => switchRoom(room.id)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs whitespace-nowrap transition-all"
              style={{
                backgroundColor: activeRoom === room.id ? `${currentColors.primary}20` : 'transparent',
                color: activeRoom === room.id ? currentColors.primary : currentColors.textMuted
              }}
              title={room.description}
            >
              <Icon className="w-3.5 h-3.5" />
              {room.name.split(' ')[0]}
            </button>
          );
        })}
      </div>

      {/* Messages Area */}
      <div 
        className="h-80 overflow-y-auto p-4 space-y-3"
        style={{ backgroundColor: currentColors.background }}
      >
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <RefreshCw className="w-6 h-6 animate-spin" style={{ color: currentColors.primary }} />
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <MessageSquare className="w-12 h-12 mb-2" style={{ color: `${currentColors.primary}30` }} />
            <p className="text-sm" style={{ color: currentColors.textMuted }}>
              No messages yet. Start the conversation!
            </p>
          </div>
        ) : (
          messages.map((msg, idx) => {
            const isOwnMessage = msg.user_id === user?.id;
            return (
              <div
                key={msg.id || idx}
                className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`max-w-[80%] ${isOwnMessage ? 'order-2' : ''}`}>
                  {!isOwnMessage && (
                    <div className="flex items-center gap-2 mb-1">
                      <div 
                        className="w-6 h-6 rounded-full flex items-center justify-center text-xs"
                        style={{ backgroundColor: `${currentColors.secondary}20` }}
                      >
                        {msg.user_avatar ? (
                          <img src={msg.user_avatar} alt="" className="w-6 h-6 rounded-full" />
                        ) : (
                          <span style={{ color: currentColors.secondary }}>
                            {msg.user_name?.charAt(0)?.toUpperCase()}
                          </span>
                        )}
                      </div>
                      <span className="text-xs font-medium" style={{ color: currentColors.text }}>
                        {msg.user_name}
                      </span>
                    </div>
                  )}
                  <div 
                    className={`px-4 py-2 rounded-2xl ${
                      isOwnMessage ? 'rounded-br-sm' : 'rounded-bl-sm'
                    }`}
                    style={{
                      backgroundColor: isOwnMessage 
                        ? currentColors.primary 
                        : `${currentColors.textMuted}15`,
                      color: isOwnMessage ? '#fff' : currentColors.text
                    }}
                  >
                    <p className="text-sm break-words">{msg.content}</p>
                  </div>
                  <div 
                    className={`text-xs mt-1 ${isOwnMessage ? 'text-right' : ''}`}
                    style={{ color: currentColors.textMuted }}
                  >
                    {formatTime(msg.created_at)}
                  </div>
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Online Users */}
      {onlineUsers.length > 0 && (
        <div 
          className="px-4 py-2 flex items-center gap-2 overflow-x-auto"
          style={{ 
            backgroundColor: `${currentColors.primary}05`,
            borderTop: `1px solid ${currentColors.border}`
          }}
        >
          <Users className="w-4 h-4 flex-shrink-0" style={{ color: currentColors.textMuted }} />
          <div className="flex -space-x-2">
            {onlineUsers.slice(0, 5).map((u, i) => (
              <div
                key={u.user_id}
                className="w-7 h-7 rounded-full border-2 flex items-center justify-center text-xs"
                style={{ 
                  backgroundColor: `${currentColors.secondary}20`,
                  borderColor: currentColors.background,
                  zIndex: 5 - i
                }}
                title={u.user_name}
              >
                {u.user_avatar ? (
                  <img src={u.user_avatar} alt="" className="w-full h-full rounded-full" />
                ) : (
                  <span style={{ color: currentColors.secondary }}>
                    {u.user_name?.charAt(0)?.toUpperCase()}
                  </span>
                )}
              </div>
            ))}
            {onlineUsers.length > 5 && (
              <div
                className="w-7 h-7 rounded-full border-2 flex items-center justify-center text-xs"
                style={{ 
                  backgroundColor: currentColors.primary,
                  borderColor: currentColors.background,
                  color: '#fff'
                }}
              >
                +{onlineUsers.length - 5}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Input Area */}
      <div 
        className="p-3"
        style={{ borderTop: `1px solid ${currentColors.border}` }}
      >
        {!chatAccess.has_access ? (
          <div className="flex items-center gap-2 py-2 px-3 rounded-lg" style={{ backgroundColor: '#fef2f2' }}>
            <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
            <p className="text-sm text-red-600">
              {chatAccess.message || 'Your chat access has been restricted.'}
            </p>
          </div>
        ) : token ? (
          <form onSubmit={handleSendMessage} className="flex gap-2">
            <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Type a message..."
              className="flex-1 px-4 py-2 rounded-full text-sm outline-none"
              style={{
                backgroundColor: `${currentColors.textMuted}10`,
                color: currentColors.text,
                border: `1px solid ${currentColors.border}`
              }}
              maxLength={1000}
              disabled={sending}
            />
            <button
              type="submit"
              disabled={!newMessage.trim() || sending}
              className="w-10 h-10 rounded-full flex items-center justify-center transition-all disabled:opacity-50"
              style={{ 
                backgroundColor: currentColors.primary,
                color: '#fff'
              }}
              data-testid="send-message-btn"
            >
              <Send className="w-4 h-4" />
            </button>
          </form>
        ) : (
          <p className="text-center text-sm py-2" style={{ color: currentColors.textMuted }}>
            Please login to chat
          </p>
        )}}
      </div>
    </div>
  );
}
